import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, signal, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { form, FormField } from '@angular/forms/signals';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  ContactDetector,
  GouvDvfDataDto,
  PropertyFinancialsDto,
  PropertyId,
  PropertyLocationDto,
  PropertyResidenceType,
  PropertyTaxesAndFeesDto,
  PropertyType,
} from '@workspace/data-types';
import { AbstractSignalFormComponent, AlertAuraComponent, AuraColorType } from '@workspace/ng-aura';
import { PropertyDataCleaner } from '../../../utils/property-data.cleaner';
import { PropertyDvfMarketReferenceComponent } from '../../property-dvf-market-reference/property-dvf-market-reference.component';

export interface PropertyTaxesAndFeesModel {
  notaryFeesCents: number | null;
  propertyTaxCents: number | null;
  housingTaxCents: number | null;
  wasteCollectionTaxCents: number | null;
}

export interface PropertyFinancialsModel {
  sellingPriceCents: number | null;
  chargesCents: number | null;
  coOwnershipChargesCents: number | null;
  precisions: string;
  taxesAndFees: PropertyTaxesAndFeesModel;
}

@Component({
  selector: 'etxe-property-financials-form',
  standalone: true,
  imports: [
    CommonModule,
    FormField,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatTooltipModule,
    AlertAuraComponent,
    PropertyDvfMarketReferenceComponent,
  ],
  templateUrl: './financials-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyFinancialsFormComponent extends AbstractSignalFormComponent<
  PropertyFinancialsDto,
  PropertyFinancialsModel
> {
  propertyId = input<PropertyId | undefined>();
  propertyIsCoOwnership = input<boolean | undefined>(false);
  residenceType = input<PropertyResidenceType | undefined>();
  habitableSurface = input<number | undefined>();
  isNewBuild = input<boolean | undefined>(false);
  propertyType = input<PropertyType | undefined>();
  propertyLocation = input<PropertyLocationDto | undefined>();
  propertyDvf = input<GouvDvfDataDto | undefined>();

  isSecondaryResidence = computed(() => this.residenceType() === PropertyResidenceType.SECONDARY);
  auraColorType = AuraColorType;

  sellingPriceEuros = computed(() => {
    const price = this.model().sellingPriceCents;
    return price ? Math.round(price / 100) : null;
  });

  pricePerSquareMeter = computed(() => {
    const price = this.model().sellingPriceCents;
    const surface = this.habitableSurface();
    if (!price || !surface || surface <= 0) return null;
    return Math.round(price / 100 / surface);
  });

  override model: WritableSignal<PropertyFinancialsModel> = signal<PropertyFinancialsModel>({
    sellingPriceCents: null,
    chargesCents: null,
    coOwnershipChargesCents: null,
    precisions: '',
    taxesAndFees: {
      notaryFeesCents: null,
      propertyTaxCents: null,
      housingTaxCents: null,
      wasteCollectionTaxCents: null,
    },
  });

  hasContactInPrecisions = computed(() => {
    const text = this.model().precisions || '';
    return ContactDetector.detect(text).hasContact;
  });

  override formTree = form(this.model);

  notaryFeesPercentage = computed(() => {
    const price = this.model().sellingPriceCents;
    const fees = this.model().taxesAndFees.notaryFeesCents;

    if (!price || !fees || price === 0) {
      return null;
    }
    return (fees / price) * 100;
  });

  /**
   * Calcul estimatif des frais de notaire selon les barèmes en vigueur en France :
   * - Neuf : ~2.5% à 3%
   * - Ancien : ~7.5% à 8%
   */
  calculateEstimatedNotaryFees(sellingPriceCents: number | null): number | null {
    if (!sellingPriceCents || sellingPriceCents <= 0) {
      return null;
    }

    const isNew = this.isNewBuild();
    if (isNew) {
      // Dans le neuf : ~2.5% (droits de mutation réduits à 0.715% + émoluments + frais fixes)
      // Taux effectif moyen d'environ 2.5% à 3%
      const rate = sellingPriceCents < 15_000_000 ? 0.03 : 0.025; // 3% si < 150k€, 2.5% au-delà
      return Math.round(sellingPriceCents * rate);
    } else {
      // Dans l'ancien : ~7.5% à 8%
      // 8% pour < 200k€, ~7.5% pour 200k-500k€, ~7% au-delà
      let rate: number;
      if (sellingPriceCents < 10_000_000) {
        rate = 0.085; // 8.5% pour les petits montants (< 100k€ à cause des frais fixes incompressibles)
      } else if (sellingPriceCents < 20_000_000) {
        rate = 0.08; // 8% pour 100k - 200k€
      } else if (sellingPriceCents < 50_000_000) {
        rate = 0.075; // 7.5% pour 200k - 500k€
      } else {
        rate = 0.07; // ~7% au-delà
      }
      return Math.round(sellingPriceCents * rate);
    }
  }

  updateCents(
    field: 'sellingPriceCents' | 'chargesCents' | 'coOwnershipChargesCents',
    euroValue: string | number | null,
  ) {
    const euro = typeof euroValue === 'string' ? Number.parseFloat(euroValue) : euroValue;
    const cents =
      euro === null || Number.isNaN(euro as number) ? null : Math.max(0, Math.round((euro as number) * 100));

    this.model.update((m) => {
      const updated = { ...m, [field]: cents };
      if (field === 'sellingPriceCents') {
        // Recalcul automatique des frais de notaire indicatifs
        const notaryFees = this.calculateEstimatedNotaryFees(cents);
        updated.taxesAndFees = {
          ...updated.taxesAndFees,
          notaryFeesCents: notaryFees,
        };
      }
      return updated;
    });
  }

  updateTaxCents(field: keyof PropertyTaxesAndFeesModel, euroValue: string | number | null) {
    const euro = typeof euroValue === 'string' ? Number.parseFloat(euroValue) : euroValue;
    const cents =
      euro === null || Number.isNaN(euro as number) ? null : Math.max(0, Math.round((euro as number) * 100));
    this.model.update((m) => ({
      ...m,
      taxesAndFees: {
        ...m.taxesAndFees,
        [field]: cents,
      },
    }));
  }

  getEuro(cents: number | null | undefined): number | null {
    if (cents === null || cents === undefined) return null;
    return cents / 100;
  }

  get formattedSellingPrice(): string {
    const cents = this.model().sellingPriceCents;
    if (cents === null || cents === undefined) return '';
    return (cents / 100).toLocaleString('fr-FR');
  }

  onSellingPriceInput(raw: string) {
    // Supprime tous les espaces et caractères non numériques
    const cleaned = raw.replace(/\s+/g, '').replace(/,/g, '.');
    if (!cleaned) {
      this.updateCents('sellingPriceCents', null);
      return;
    }
    const num = Number.parseFloat(cleaned);
    this.updateCents('sellingPriceCents', Number.isNaN(num) ? null : num);
  }

  protected override buildModelFromDto(dto: PropertyFinancialsDto): PropertyFinancialsModel {
    const sellingPriceCents = dto?.sellingPriceCents ? Math.max(0, dto.sellingPriceCents) : null;
    // Si déjà présent dans le DTO on conserve, sinon calcul automatique
    const notaryFeesCents =
      dto?.taxesAndFees?.notaryFeesCents != null
        ? Math.max(0, dto.taxesAndFees.notaryFeesCents)
        : this.calculateEstimatedNotaryFees(sellingPriceCents);

    return {
      sellingPriceCents,
      chargesCents: dto?.chargesCents ? Math.max(0, dto.chargesCents) : null,
      coOwnershipChargesCents: dto?.coOwnershipChargesCents ? Math.max(0, dto.coOwnershipChargesCents) : null,
      precisions: dto?.precisions ?? '',
      taxesAndFees: {
        notaryFeesCents,
        propertyTaxCents: dto?.taxesAndFees?.propertyTaxCents
          ? Math.max(0, dto.taxesAndFees.propertyTaxCents)
          : null,
        housingTaxCents: dto?.taxesAndFees?.housingTaxCents
          ? Math.max(0, dto.taxesAndFees.housingTaxCents)
          : null,
        wasteCollectionTaxCents: dto?.taxesAndFees?.wasteCollectionTaxCents
          ? Math.max(0, dto.taxesAndFees.wasteCollectionTaxCents)
          : null,
      },
    };
  }

  protected override buildDtoFromModel(model: PropertyFinancialsModel): PropertyFinancialsDto {
    const rawDto: PropertyFinancialsDto = {
      ...(this.dto() || ({} as PropertyFinancialsDto)),
      sellingPriceCents: model.sellingPriceCents ?? 0,
      chargesCents: model.chargesCents ?? undefined,
      coOwnershipChargesCents: model.coOwnershipChargesCents ?? undefined,
      precisions: model.precisions || undefined,
      taxesAndFees: {
        notaryFeesCents: model.taxesAndFees.notaryFeesCents ?? undefined,
        propertyTaxCents: model.taxesAndFees.propertyTaxCents ?? undefined,
        housingTaxCents: model.taxesAndFees.housingTaxCents ?? undefined,
        wasteCollectionTaxCents: model.taxesAndFees.wasteCollectionTaxCents ?? undefined,
      } as PropertyTaxesAndFeesDto,
    };

    return PropertyDataCleaner.deepClean(rawDto) as PropertyFinancialsDto;
  }
}
